import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Shield, Award, ArrowRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
export function About() {
  return <div className="bg-navy-900 min-h-screen" data-id="element-113">
      <PageHeader title="About Marvesta Corp Bali" subtitle="Delivering international corporate standards with deep local expertise in Indonesia's dynamic market." backgroundImage="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80" data-id="element-114" />

      {/* Company Story */}
      <section className="py-24" data-id="element-115">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-116">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-117">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="w-full lg:w-1/2" data-id="element-118">
              <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-119">
                Our Story
              </h2>
              <p className="font-body text-gray-400 text-lg leading-relaxed mb-6" data-id="element-120">
                Founded in Bali, Marvesta Corp emerged from a clear market need:
                investors and businesses required a single, reliable partner
                capable of handling the complexities of the Indonesian corporate
                landscape with international professionalism.
              </p>
              <p className="font-body text-gray-400 text-lg leading-relaxed mb-8" data-id="element-121">
                Today, we are a premier consulting firm specializing in Property
                Management, Accounting, Tax, Legal, and Business Licensing. We
                bridge the gap between foreign investment ambitions and local
                regulatory realities, ensuring our clients operate securely and
                profitably.
              </p>
              <div className="grid grid-cols-2 gap-8 border-t border-navy-700 pt-8" data-id="element-122">
                <div data-id="element-123">
                  <div className="font-display text-4xl font-bold text-gold mb-2" data-id="element-124">
                    10+
                  </div>
                  <div className="font-sub text-sm text-gray-400 uppercase tracking-wider" data-id="element-125">
                    Years Experience
                  </div>
                </div>
                <div data-id="element-126">
                  <div className="font-display text-4xl font-bold text-gold mb-2" data-id="element-127">
                    500+
                  </div>
                  <div className="font-sub text-sm text-gray-400 uppercase tracking-wider" data-id="element-128">
                    Clients Served
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="w-full lg:w-1/2" data-id="element-129">
              <div className="relative rounded-card overflow-hidden shadow-card h-[600px]" data-id="element-130">
                <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Corporate Meeting" className="w-full h-full object-cover" data-id="element-131" />
                <div className="absolute inset-0 bg-navy-900/20" data-id="element-132"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-navy-800 border-y border-navy-700" data-id="element-133">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-134">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-135">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-navy-900 p-12 rounded-card border border-navy-700" data-id="element-136">
              <Target size={40} className="text-gold mb-6" data-id="element-137" />
              <h3 className="font-heading text-3xl font-semibold text-white mb-4" data-id="element-138">
                Our Mission
              </h3>
              <p className="font-body text-gray-400 text-lg leading-relaxed" data-id="element-139">
                To provide integrated, transparent, and premium corporate
                solutions that empower investors and businesses to thrive in
                Indonesia, mitigating risk while maximizing operational
                efficiency and asset value.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-navy-900 p-12 rounded-card border border-navy-700" data-id="element-140">
              <Eye size={40} className="text-gold mb-6" data-id="element-141" />
              <h3 className="font-heading text-3xl font-semibold text-white mb-4" data-id="element-142">
                Our Vision
              </h3>
              <p className="font-body text-gray-400 text-lg leading-relaxed" data-id="element-143">
                To be the most trusted and authoritative corporate consulting
                and property management firm in Indonesia, recognized for our
                unwavering commitment to excellence, integrity, and client
                success.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24" data-id="element-144">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-145">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-146">
            <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-147">
              Our Core Values
            </h2>
            <p className="font-body text-gray-400 text-lg" data-id="element-148">
              The principles that guide our decisions and define our corporate
              culture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-149">
            {[{
            icon: <Shield size={32} data-id="element-150" />,
            title: 'Integrity',
            desc: 'We operate with absolute transparency and strict adherence to legal and ethical standards.'
          }, {
            icon: <Award size={32} data-id="element-151" />,
            title: 'Excellence',
            desc: 'We deliver premium quality in every report, consultation, and management decision.'
          }, {
            icon: <Target size={32} data-id="element-152" />,
            title: 'Commitment',
            desc: "We view our clients' investments as our own, dedicating ourselves to long-term partnership."
          }].map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="text-center p-8" data-id="element-153">
                <div className="w-20 h-20 mx-auto bg-navy-800 rounded-full flex items-center justify-center text-gold mb-6 border border-navy-700" data-id="element-154">
                  {value.icon}
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-4" data-id="element-155">
                  {value.title}
                </h3>
                <p className="font-body text-gray-400" data-id="element-156">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-24 bg-navy-800 text-center border-t border-navy-700" data-id="element-157">
        <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-158">
          <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-159">
            Partner with Marvesta Corp
          </h2>
          <p className="font-body text-gray-400 text-lg mb-10" data-id="element-160">
            Experience the difference of working with Bali's premier corporate
            consulting firm.
          </p>
          <Button to="/contact" icon={<ArrowRight size={18} data-id="element-162" />} data-id="element-161">
            Contact Our Team
          </Button>
        </div>
      </section>
    </div>;
}